import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { SiteConfigManager } from '@atlas/core';
import { ITEM_MORE, useGetLastVisibleIndex } from '@atlas/ui';

import { PLATFORM_TYPES } from 'common/constants';
import { HeaderMenuList } from 'components/Layout/Header/HeaderMenuList';

const MENU_ITEM_MORE = {
  id: ITEM_MORE,
  name: 'more',
};

const { DESKTOP, MULTIPLATFORM } = PLATFORM_TYPES;

export const HeaderMenu = ({ hasNativeLinks } = {}) => {
  const wrapRef = useRef();
  const topNavigation = SiteConfigManager.getConfig('navigation.top', []);
  const items = useMemo(
    () =>
      topNavigation
        .filter(({ platform_type }) => platform_type === MULTIPLATFORM || platform_type === DESKTOP)
        .sort((a, b) => a.order_index - b.order_index),
    [topNavigation],
  );

  const lastVisibleIndex = useGetLastVisibleIndex(items, wrapRef, '.header-menu');
  const [isMoreActive, setIsMoreActive] = useState(false);

  const handleCloseClick = useCallback(() => {
    if (isMoreActive) {
      setIsMoreActive(false);
    }
  }, [isMoreActive]);

  const allHeaderMenuItems = useMemo(() => {
    const itemsWithCloseClick = items.map(item => ({
      ...item,
      onClick: handleCloseClick,
    }));

    if (!lastVisibleIndex) {
      return {
        headerItemsDropdown: [],
        headerItemsMain: itemsWithCloseClick,
        headerItemsWithMore: [...items, MENU_ITEM_MORE],
      };
    }

    const headerItemsDropdown = itemsWithCloseClick.slice(lastVisibleIndex);

    return {
      headerItemsDropdown,
      headerItemsMain: [
        ...itemsWithCloseClick.slice(0, lastVisibleIndex),
        {
          ...MENU_ITEM_MORE,
          active: isMoreActive,
          onClickMore: () => setIsMoreActive(prev => !prev),
        },
      ],
      headerItemsWithMore: [...items, MENU_ITEM_MORE],
    };
  }, [items, lastVisibleIndex, isMoreActive, handleCloseClick]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        handleCloseClick();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleCloseClick]);

  if (Array.isArray(topNavigation) && topNavigation.length > 0) {
    return (
      <div ref={wrapRef}>
        <HeaderMenuList
          className="header-menu header-menu-fake"
          hasNativeLinks={hasNativeLinks}
          isFake
          items={allHeaderMenuItems.headerItemsWithMore}
        />
        <HeaderMenuList
          className="header-menu"
          hasNativeLinks={hasNativeLinks}
          items={allHeaderMenuItems.headerItemsMain}
        />
        {isMoreActive && (
          <HeaderMenuList
            className="header-menu header-menu-dropdown"
            hasNativeLinks={hasNativeLinks}
            items={allHeaderMenuItems.headerItemsDropdown}
          />
        )}
      </div>
    );
  }
  return null;
};

HeaderMenu.propTypes = {
  hasNativeLinks: PropTypes.bool,
};
